// components/Form2316Summary.js
import React, { useMemo } from 'react';
import { formatCurrency, formatPercentage } from '../utils/numberUtils';
import form2316API from '../api/form2316API';

const Form2316Summary = ({ client, records, selectedYear, yearSummary, summaryData, onExportCSV, onExportSummary }) => {
    const exemptionStatusOptions = form2316API.getExemptionStatusOptions();
    const statusOptions = form2316API.getStatusOptions();

    // Calculate statistics
    const statistics = useMemo(() => {
        const filteredRecords = selectedYear
            ? records.filter(r => r.year === selectedYear)
            : records;

        if (filteredRecords.length === 0) {
            return {
                averageSalary: 0,
                averageTaxRate: 0,
                totalEmployees: 0,
                certifiedCount: 0,
                draftCount: 0,
                exemptionBreakdown: {}
            };
        }

        const totalGrossCompensation = filteredRecords.reduce((sum, r) => sum + (r.grossCompensationIncome || 0), 0);
        const totalTaxWithheld = filteredRecords.reduce((sum, r) => sum + (r.taxWithheld || 0), 0);
        
        // Calculate exemption breakdown
        const exemptionBreakdown = {};
        filteredRecords.forEach(record => {
            const status = record.employeeExemptionStatus || 'Z';
            if (!exemptionBreakdown[status]) {
                exemptionBreakdown[status] = {
                    count: 0,
                    totalCompensation: 0,
                    totalTax: 0
                };
            }
            exemptionBreakdown[status].count++;
            exemptionBreakdown[status].totalCompensation += record.grossCompensationIncome || 0;
            exemptionBreakdown[status].totalTax += record.taxWithheld || 0;
        });

        return {
            averageSalary: totalGrossCompensation / filteredRecords.length,
            averageTaxRate: totalGrossCompensation > 0 ? (totalTaxWithheld / totalGrossCompensation) * 100 : 0,
            totalEmployees: new Set(filteredRecords.map(r => r.employeeTin)).size,
            certifiedCount: filteredRecords.filter(r => r.status === 'Certified').length,
            draftCount: filteredRecords.filter(r => r.status === 'Draft').length,
            exemptionBreakdown
        };
    }, [records, selectedYear]);

    // Get status breakdown from API data or calculate
    const statusBreakdown = summaryData?.data?.statusSummary || [];

    // Get exemption summary from API data or calculate
    const exemptionSummary = summaryData?.data?.exemptionSummary || [];

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                        <div className="ml-4">
                            <h4 className="text-sm font-medium text-gray-500">Total Tax Withheld</h4>
                            <p className="text-xl font-semibold text-teal-600">
                                {formatCurrency(yearSummary.totalTaxWithheld)}
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </div>
                        </div>
                        <div className="ml-4">
                            <h4 className="text-sm font-medium text-gray-500">Gross Compensation</h4>
                            <p className="text-xl font-semibold text-green-600">
                                {formatCurrency(yearSummary.totalGrossCompensation)}
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 2.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                </svg>
                            </div>
                        </div>
                        <div className="ml-4">
                            <h4 className="text-sm font-medium text-gray-500">Employees</h4>
                            <p className="text-xl font-semibold text-purple-600">
                                {statistics.totalEmployees}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                {yearSummary.recordCount} records
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                        </div>
                        <div className="ml-4">
                            <h4 className="text-sm font-medium text-gray-500">Certified</h4>
                            <p className="text-xl font-semibold text-yellow-600">
                                {statistics.certifiedCount}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                {formatPercentage((statistics.certifiedCount / yearSummary.recordCount) * 100)} of total
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts and Detailed Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Status Breakdown */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4">Status Breakdown</h4>
                    <div className="space-y-3">
                        {statusBreakdown.map((item) => {
                            const statusOption = statusOptions.find(opt => opt.value === item._id);
                            const color = statusOption?.color || 'gray';
                            return (
                                <div key={item._id} className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className={`w-3 h-3 rounded-full bg-${color}-500 mr-2`}></div>
                                        <span className="text-sm text-gray-600">{item._id}</span>
                                    </div>
                                    <div className="text-sm font-medium text-gray-900">
                                        {item.recordCount} records
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Exemption Status Breakdown */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4">Exemption Status Breakdown</h4>
                    <div className="space-y-3">
                        {exemptionSummary.map((item) => {
                            const option = exemptionStatusOptions.find(opt => opt.value === item._id);
                            return (
                                <div key={item._id} className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className="w-3 h-3 rounded-full bg-teal-500 mr-2"></div>
                                        <span className="text-sm text-gray-600">
                                            {option ? option.label.split(' - ')[0] : item._id}
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-medium text-gray-900">
                                            {item.recordCount} employees
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            Avg Tax Rate: {formatPercentage(item.averageTaxRate || 0)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Tax Summary */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Tax Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                        <div className="text-sm text-gray-500">Average Salary</div>
                        <div className="text-lg font-semibold text-gray-900">
                            {formatCurrency(statistics.averageSalary)}
                        </div>
                    </div>
                    <div>
                        <div className="text-sm text-gray-500">Average Tax Rate</div>
                        <div className="text-lg font-semibold text-teal-600">
                            {formatPercentage(statistics.averageTaxRate)}
                        </div>
                    </div>
                    <div>
                        <div className="text-sm text-gray-500">Total Tax Due</div>
                        <div className="text-lg font-semibold text-red-600">
                            {formatCurrency(yearSummary.totalTaxDue)}
                        </div>
                    </div>
                    <div>
                        <div className="text-sm text-gray-500">Total Tax Refund</div>
                        <div className="text-lg font-semibold text-green-600">
                            {formatCurrency(yearSummary.totalTaxRefund)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Yearly Comparison (if multiple years) */}
            {summaryData?.data?.summary && summaryData.data.summary.length > 1 && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4">Yearly Comparison</h4>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Year
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Records
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Employees
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Gross Compensation
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tax Withheld
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tax Rate
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {summaryData.data.summary.map((yearData) => (
                                    <tr key={yearData.year} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                                            {yearData.year}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-500">
                                            {yearData.recordCount}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-500">
                                            {yearData.employeeCount || 'N/A'}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-900">
                                            {formatCurrency(yearData.totalGrossCompensation)}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-teal-600">
                                            {formatCurrency(yearData.totalTaxWithheld)}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-900">
                                            {formatPercentage(
                                                yearData.totalGrossCompensation > 0
                                                    ? (yearData.totalTaxWithheld / yearData.totalGrossCompensation) * 100
                                                    : 0
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Export Buttons */}
            <div className="flex justify-end space-x-3">
                <button
                    type="button"
                    onClick={onExportCSV}
                    className="px-4 py-2 text-sm text-green-600 hover:text-green-800 hover:bg-green-100 border border-green-300 rounded-md transition-colors flex items-center"
                >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export Data
                </button>
                <button
                    type="button"
                    onClick={onExportSummary}
                    className="px-4 py-2 text-sm text-teal-600 hover:text-teal-800 hover:bg-teal-100 border border-teal-300 rounded-md transition-colors flex items-center"
                >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export Summary
                </button>
            </div>
        </div>
    );
};

export default Form2316Summary;